import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Paperclip, X, Send, CheckCircle } from 'lucide-react';
import { useUserStore } from '@auw/stores';
import { useChatStore } from '@auw/stores';
import { submitContactInquiry } from '@auw/api-client';
import { Button } from '../../common';
import { SelectDropdown } from '../../common';
import {
  Divider,
  SectionBlock,
  SectionDesc,
  SectionHeader,
  SectionTitle,
} from '../styles';

const INQUIRY_TYPES = [
  'typeGeneral',
  'typeBug',
  'typeFeature',
  'typeAccount',
  'typePayment',
  'typeOther',
] as const;

const MAX_FILE_SIZE = 10 * 1024 * 1024;
const ACCEPTED_TYPES = '.jpg,.jpeg,.png,.pdf';

const FormGroup = styled.div`
  margin-bottom: 16px;
  &:last-child { margin-bottom: 0; }
`;

const Label = styled.label`
  display: block;
  font-size: 0.8125rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
  margin-bottom: 6px;
`;

const Input = styled.input`
  width: 100%;
  padding: 9px 12px;
  font-size: 0.8125rem;
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.bgSecondary};
  color: ${({ theme }) => theme.colors.textPrimary};
  outline: none;
  transition: border-color 0.15s ease;

  &::placeholder {
    color: ${({ theme }) => theme.colors.textMuted};
  }
  &:focus {
    border-color: ${({ theme }) => theme.colors.accent};
  }
`;

const InquirySelect = styled(SelectDropdown)`
  width: 100%;

  button[aria-haspopup='listbox'] {
    min-height: 38px;
    padding: 0 12px;
    border-radius: 8px;
    font-size: 0.8125rem;
  }

  [role='listbox'] {
    margin-top: 4px;
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(32, 32, 34, 0.98)' : 'rgba(255, 255, 255, 0.98)'};
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  min-height: 100px;
  padding: 10px 12px;
  font-size: 0.8125rem;
  font-family: inherit;
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.bgSecondary};
  color: ${({ theme }) => theme.colors.textPrimary};
  outline: none;
  resize: vertical;
  transition: border-color 0.15s ease;
  line-height: 1.5;

  &::placeholder {
    color: ${({ theme }) => theme.colors.textMuted};
  }
  &:focus {
    border-color: ${({ theme }) => theme.colors.accent};
  }
`;

const FileRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const FileChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 10px;
  font-size: 0.75rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textSecondary};
  background: ${({ theme }) => theme.colors.bgTertiary};
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  border-radius: 6px;
  max-width: 240px;
`;

const FileName = styled.span`
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  min-width: 0;
`;

const FileRemoveBtn = styled.button`
  border: none;
  background: transparent;
  padding: 0;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.textMuted};
  display: flex;
  align-items: center;
  flex-shrink: 0;
  transition: color 0.15s;

  &:hover {
    color: ${({ theme }) => theme.colors.error};
  }
`;

const FileHint = styled.span`
  font-size: 0.6875rem;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const ErrorText = styled.span`
  display: block;
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.error};
  margin-top: 4px;
`;

const SubmitRow = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
`;

const SuccessWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 48px 24px;
  text-align: center;
`;

const SuccessIcon = styled.div`
  color: ${({ theme }) => theme.colors.success ?? '#22c55e'};
`;

const SuccessTitle = styled.div`
  font-size: 1rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const SuccessDesc = styled.div`
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.colors.textMuted};
`;

interface FormErrors {
  email?: string;
  type?: string;
  content?: string;
}

export const ContactTab: React.FC = () => {
  const { t } = useTranslation('settings');
  const user = useUserStore((s) => s.user);
  const addToast = useChatStore((s) => s.addToast);

  const [email, setEmail] = useState(() => user?.email ?? '');
  const [inquiryType, setInquiryType] = useState('');
  const [content, setContent] = useState('');

  const inquiryOptions = useMemo(
    () => INQUIRY_TYPES.map((key) => ({ value: key, label: t(`contact.${key}`) })),
    [t],
  );
  const [file, setFile] = useState<File | null>(null);
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validate = useCallback((): FormErrors => {
    const next: FormErrors = {};
    if (!email.trim()) {
      next.email = t('contact.emailRequired');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      next.email = t('contact.emailInvalid');
    }
    if (!inquiryType) next.type = t('contact.typeRequired');
    if (!content.trim()) next.content = t('contact.contentRequired');
    return next;
  }, [content, email, inquiryType, t]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > MAX_FILE_SIZE) {
      addToast({ type: 'warning', title: t('contact.attachmentHint') });
      return;
    }
    setFile(f);
  };

  const handleSubmit = async () => {
    const validationErrors = validate();
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) return;

    setSubmitting(true);
    try {
      await submitContactInquiry({
        email: email.trim(),
        inquiryType,
        content: content.trim(),
        file,
      });
      setSubmitted(true);
      addToast({
        type: 'success',
        title: t('contact.submitSuccess'),
        description: t('contact.submitSuccessDesc'),
      });
    } catch {
      addToast({ type: 'error', title: t('contact.submitFail') });
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <SuccessWrap>
        <SuccessIcon><CheckCircle size={48} /></SuccessIcon>
        <SuccessTitle>{t('contact.submitSuccess')}</SuccessTitle>
        <SuccessDesc>{t('contact.submitSuccessDesc')}</SuccessDesc>
      </SuccessWrap>
    );
  }

  return (
    <>
      <SectionBlock>
        <SectionHeader>
          <SectionTitle>{t('contact.title')}</SectionTitle>
        </SectionHeader>
        <SectionDesc>{t('contact.description')}</SectionDesc>
        <Divider />

        <FormGroup>
          <Label>{t('contact.email')}</Label>
          <Input
            type="email"
            value={email}
            onChange={(e) => { setEmail(e.target.value); setErrors((p) => ({ ...p, email: undefined })); }}
            placeholder={t('contact.emailPlaceholder')}
          />
          {errors.email && <ErrorText>{errors.email}</ErrorText>}
        </FormGroup>

        <FormGroup>
          <Label>{t('contact.inquiryType')}</Label>
          <InquirySelect
            options={inquiryOptions}
            value={inquiryType}
            onChange={(v) => { setInquiryType(v); setErrors((p) => ({ ...p, type: undefined })); }}
            placeholder={t('contact.selectType')}
            size="sm"
          />
          {errors.type && <ErrorText>{errors.type}</ErrorText>}
        </FormGroup>

        <FormGroup>
          <Label>{t('contact.content')}</Label>
          <Textarea
            value={content}
            onChange={(e) => { setContent(e.target.value); setErrors((p) => ({ ...p, content: undefined })); }}
            placeholder={t('contact.contentPlaceholder')}
          />
          {errors.content && <ErrorText>{errors.content}</ErrorText>}
        </FormGroup>

        <FormGroup>
          <Label>{t('contact.attachment')}</Label>
          <FileRow>
            {file ? (
              <FileChip>
                <Paperclip size={12} />
                <FileName>{file.name}</FileName>
                <FileRemoveBtn
                  type="button"
                  onClick={() => { setFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}
                  aria-label={t('contact.removeFile')}
                >
                  <X size={12} />
                </FileRemoveBtn>
              </FileChip>
            ) : (
              <Button
                variant="secondary"
                size="sm"
                leftIcon={<Paperclip size={13} />}
                onClick={() => fileInputRef.current?.click()}
              >
                {t('contact.attachFile')}
              </Button>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPTED_TYPES}
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />
          </FileRow>
          <FileHint>{t('contact.attachmentHint')}</FileHint>
        </FormGroup>

        <SubmitRow>
          <Button
            variant="primary"
            size="md"
            leftIcon={<Send size={14} />}
            isLoading={submitting}
            disabled={submitting}
            onClick={handleSubmit}
          >
            {submitting ? t('contact.submitting') : t('contact.submit')}
          </Button>
        </SubmitRow>
      </SectionBlock>
    </>
  );
};
